import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import FilterBar, { type FilterGroup } from './components/FilterBar';
import { appleVideos, movieVideos, tvVideos, type Video } from '@/mocks/videos';

type CategoryKey = 'movie' | 'tv' | 'apple';

interface CategoryConfig {
  key: CategoryKey;
  name: string;
  intro: string;
  data: Video[];
  pageSize: number;
}

const configs: Record<CategoryKey, CategoryConfig> = {
  movie: {
    key: 'movie',
    name: '电影频道',
    intro: '汇聚最新与经典电影资源，涵盖剧情、动作、科幻、悬疑等多种类型，总有一部适合你。',
    data: movieVideos,
    pageSize: 8,
  },
  tv: {
    key: 'tv',
    name: '电视剧频道',
    intro: '热播与经典剧集持续更新，古装、都市、悬疑、家庭题材一站看全。',
    data: tvVideos,
    pageSize: 8,
  },
  apple: {
    key: 'apple',
    name: '苹果产品视频',
    intro: '聚焦苹果品牌相关视频内容，包括新品发布、专业评测与使用教程，助你了解每一款产品的真实表现。',
    data: appleVideos,
    pageSize: 6,
  },
};

const ALL = '全部';

const sortOptions = [
  { label: '按最新', value: 'latest' },
  { label: '按最热', value: 'hot' },
];

const appleTabs = [ALL, '新品', '评测', '教程'];

function unique(values: string[]): string[] {
  return Array.from(new Set(values));
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const config = type && type in configs ? configs[type as CategoryKey] : undefined;

  const [region, setRegion] = useState(ALL);
  const [year, setYear] = useState(ALL);
  const [genre, setGenre] = useState(ALL);
  const [appleTab, setAppleTab] = useState(ALL);
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);

  const title = config?.name ?? '频道';

  useEffect(() => {
    setRegion(ALL);
    setYear(ALL);
    setGenre(ALL);
    setAppleTab(ALL);
    setSort('latest');
    setPage(1);
  }, [type]);

  useEffect(() => {
    if (config) {
      document.title = `苹果视频-${config.name}-丰富视频资源在线观看`;
    }
  }, [config]);

  useEffect(() => {
    setPage(1);
  }, [region, year, genre, appleTab, sort]);

  const filterGroups: FilterGroup[] = useMemo(() => {
    if (!config || config.key === 'apple') return [];
    const data = config.data;
    return [
      {
        key: 'region',
        label: '地区',
        options: [ALL, ...unique(data.map((item) => item.region))].map((value) => ({
          label: value,
          value,
        })),
      },
      {
        key: 'year',
        label: '年份',
        options: [ALL, ...unique(data.map((item) => item.year)).sort().reverse()].map(
          (value) => ({ label: value, value }),
        ),
      },
      {
        key: 'genre',
        label: '类型',
        options: [ALL, ...unique(data.flatMap((item) => item.genres))].map((value) => ({
          label: value,
          value,
        })),
      },
    ];
  }, [config]);

  const filtered = useMemo(() => {
    if (!config) return [];
    let list = [...config.data];
    if (config.key === 'apple') {
      if (appleTab !== ALL) list = list.filter((item) => item.appleCategory === appleTab);
    } else {
      if (region !== ALL) list = list.filter((item) => item.region === region);
      if (year !== ALL) list = list.filter((item) => item.year === year);
      if (genre !== ALL) list = list.filter((item) => item.genres.includes(genre));
    }
    if (sort === 'latest') {
      list.sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1));
    } else {
      list.sort((a, b) => Number.parseFloat(b.views) - Number.parseFloat(a.views));
    }
    return list;
  }, [config, region, year, genre, appleTab, sort]);

  const pageSize = config?.pageSize ?? 8;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'region') setRegion(value);
    if (key === 'year') setYear(value);
    if (key === 'genre') setGenre(value);
  };

  const handleReset = () => {
    setRegion(ALL);
    setYear(ALL);
    setGenre(ALL);
    setAppleTab(ALL);
    setSort('latest');
    setPage(1);
  };

  if (!config) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="max-w-md rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true" />
            <h1 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              频道不存在或已下线
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              请从下方入口选择想要浏览的内容频道。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />

      <main className="flex-1">
        <div className="bg-background-50 px-4 pb-6 pt-6 md:px-8 md:pb-8 md:pt-8">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: title }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            {title}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {config.intro}
          </p>
        </div>

        <div className="px-4 py-8 md:px-8 md:py-10">
          {config.key === 'apple' ? (
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                {appleTabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setAppleTab(tab)}
                    className={`cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      appleTab === tab
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2">
                <span className="whitespace-nowrap text-sm text-foreground-500">排序</span>
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <div className="mb-6">
              <FilterBar
                groups={filterGroups}
                values={{ region, year, genre }}
                onChange={handleFilterChange}
                sort={sort}
                onSortChange={setSort}
                sortOptions={sortOptions}
                onReset={handleReset}
              />
            </div>
          )}

          <p className="mb-5 text-sm text-foreground-500">
            共筛选出 <span className="font-bold text-primary-600">{filtered.length}</span> 个视频
          </p>

          {paged.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
              <i className="ri-file-search-line text-4xl text-foreground-300" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-600">
                暂无符合条件的视频，尝试重置筛选。
              </p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置筛选
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6" data-product-shop>
              {paged.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="上一页"
              >
                <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
              </button>
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setPage(index + 1)}
                  className={`h-9 w-9 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                    page === index + 1
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="下一页"
              >
                <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}